import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
  IconButton
} from '@mui/material';
import {
  Event as EventIcon,
  ArrowForward as ArrowIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';

const UpcomingEvents = ({ events, loading }) => {
  const formatLocation = (loc) => {
    if (!loc) return null;
    if (typeof loc === 'string') return loc;
    return loc.name || loc.address || null;
  };

  const safeFormatDate = (ts) => {
    try {
      const d = ts?.toDate ? ts.toDate() : new Date(ts);
      return formatDistanceToNow(d, { addSuffix: true });
    } catch { return ''; }
  };
  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          avatar={<EventIcon color="primary" />}
          title="Upcoming Events"
        />
        <CardContent sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          avatar={<EventIcon color="primary" />}
          title="Upcoming Events"
          action={
            <IconButton component={Link} to="/events">
              <ArrowIcon />
            </IconButton>
          }
        />
        <CardContent sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No upcoming events. Create one to get started!
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        avatar={<EventIcon color="primary" />}
        title="Upcoming Events"
        action={
          <IconButton component={Link} to="/events" title="View calendar">
            <ArrowIcon />
          </IconButton>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <List dense>
          {events.map((event) => {
            const eventDate = event.date?.toDate ? event.date.toDate() : new Date(event.date);
            const isToday = format(eventDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

            return (
              <ListItem
                key={event.id}
                sx={{
                  borderLeft: 3,
                  borderColor: isToday ? 'error.main' : 'secondary.main',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: 'background.default',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="subtitle2" component="span" sx={{ fontWeight: 600 }}>
                        {event.title}
                      </Typography>
                      {isToday && (
                        <Chip label="Today" size="small" color="error" sx={{ height: 20 }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'block' }}>
                      <Typography variant="body2" component="span" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {format(eventDate, 'EEEE, MMMM d, yyyy • h:mm a')}
                      </Typography>
                      {formatLocation(event.location) && (
                        <Typography variant="caption" component="span" color="text.disabled" sx={{ display: 'block' }}>
                          📍 {formatLocation(event.location)}
                        </Typography>
                      )}
                      <Typography variant="caption" component="span" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                        {safeFormatDate(event.date)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </CardContent>
    </Card>
  );
};

export default UpcomingEvents;
